import { useState } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    projectType: '',
    timeline: '',
    budget: '',
    ownHome: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const pdfUrl = 'https://drive.google.com/uc?export=download&id=15eMMNNtCIVW7xf3yCxhjsFgv-xf8Lwmu';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        projectType: formData.projectType,
        timeline: formData.timeline,
        budget: formData.budget,
        ownHome: formData.ownHome
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d6et572ohb161tfd996g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        
        // Track Meta Pixel Schedule event
        if (typeof window !== 'undefined' && (window as any).fbq) {
          (window as any).fbq('track', 'Schedule');
        }
        
        setFormData({
          name: '',
          phone: '',
          email: '',
          projectType: '',
          timeline: '',
          budget: '',
          ownHome: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const scrollToForm = () => {
    const formElement = document.getElementById('quote-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <div className="text-2xl font-bold text-gray-900">
                West Michigan Decks
              </div>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-gray-700 hover:text-gray-900 font-medium transition-colors cursor-pointer">Services</a>
              <a href="/portfolio" className="text-gray-700 hover:text-gray-900 font-medium transition-colors cursor-pointer">Portfolio</a>
              <a href="#process" className="text-gray-700 hover:text-gray-900 font-medium transition-colors cursor-pointer">Process</a>
              <a href="#faq" className="text-gray-700 hover:text-gray-900 font-medium transition-colors cursor-pointer">FAQ</a>
              <button 
                onClick={scrollToForm}
                className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>

            <button 
              className="md:hidden cursor-pointer"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
            >
              <i className={`ri-${showMobileMenu ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>
        </div>

        {showMobileMenu && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              <a href="#services" className="block text-gray-700 font-medium cursor-pointer" onClick={() => setShowMobileMenu(false)}>Services</a>
              <a href="/portfolio" className="block text-gray-700 font-medium cursor-pointer" onClick={() => setShowMobileMenu(false)}>Portfolio</a>
              <a href="#process" className="block text-gray-700 font-medium cursor-pointer" onClick={() => setShowMobileMenu(false)}>Process</a>
              <a href="#faq" className="block text-gray-700 font-medium cursor-pointer" onClick={() => setShowMobileMenu(false)}>FAQ</a>
              <button 
                onClick={() => {
                  scrollToForm();
                  setShowMobileMenu(false);
                }}
                className="w-full bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/423bf069400388d78f815aed6c30f8bd.jpg"
            alt="Beautiful custom deck"
            className="w-full h-full object-cover opacity-10"
          />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <i className="ri-map-pin-line"></i>
                Serving Grand Rapids &amp; West Michigan
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Get a Custom Deck Built in West Michigan Without Overpaying or Dealing With Delays
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                See pricing, design options, and availability for your project — request your free quote below.
              </p>

              <div className="flex flex-wrap items-center gap-6 mb-8">
                <div className="flex items-center gap-2 text-gray-700">
                  <i className="ri-check-line text-2xl text-green-600"></i>
                  <span className="font-medium">Hundreds of satisfied homeowners</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <i className="ri-time-line text-2xl text-green-600"></i>
                  <span className="font-medium">Fast response time</span>
                </div>
              </div>

              <div className="lg:hidden">
                <button 
                  onClick={scrollToForm}
                  className="w-full bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Get My Free Deck Quote
                </button>
              </div>
            </div>

            {/* Hero Form */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100" id="quote-form">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Request Your Free Quote</h3>
              <p className="text-gray-600 mb-6">Get pricing and availability in 24 hours</p>

              <form onSubmit={handleSubmit} data-readdy-form id="hero-quote-form">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
                      placeholder="(616) 555-0123"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="projectType" className="block text-sm font-semibold text-gray-700 mb-2">
                      Project Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="projectType"
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base cursor-pointer"
                    >
                      <option value="">Select project type</option>
                      <option value="New Deck">New Deck</option>
                      <option value="Replace Deck">Replace Deck</option>
                      <option value="Composite Deck">Composite Deck</option>
                      <option value="Repair">Repair</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="timeline" className="block text-sm font-semibold text-gray-700 mb-2">
                      Timeline <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base cursor-pointer"
                    >
                      <option value="">Select timeline</option>
                      <option value="ASAP">ASAP</option>
                      <option value="1-3 months">1–3 months</option>
                      <option value="3-6 months">3–6 months</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="budget" className="block text-sm font-semibold text-gray-700 mb-2">
                      What Is Your Budget? <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base cursor-pointer"
                    >
                      <option value="">Select your budget</option>
                      <option value="Under $10,000">Under $10,000</option>
                      <option value="$10,000 - $20,000">$10,000 – $20,000</option>
                      <option value="$20,000 - $35,000">$20,000 – $35,000</option>
                      <option value="$35,000 - $50,000">$35,000 – $50,000</option>
                      <option value="$50,000+">$50,000+</option>
                      <option value="Not sure yet">Not sure yet</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="ownHome" className="block text-sm font-semibold text-gray-700 mb-2">
                      Do You Own Your Home? <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="ownHome"
                      name="ownHome"
                      value={formData.ownHome}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-base cursor-pointer"
                    >
                      <option value="">Select an option</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-orange-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? 'Sending...' : 'Get My Free Deck Quote'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                      <strong>Success!</strong> We&apos;ll contact you within 24 hours with your quote.
                      {pdfUrl && (
                        <div className="mt-3">
                          <a 
                            href={pdfUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-orange-700 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-download-line"></i>
                            Download Your Free Planning Guide
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                      <strong>Error:</strong> Something went wrong. Please try again or call us directly.
                    </div>
                  )}

                  <p className="text-xs text-gray-500 text-center">
                    No obligation. Fast response. Your information is secure.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Magnet Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-orange-600">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-white rounded-full mb-6">
                <i className="ri-file-text-line text-2xl text-orange-600"></i>
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Free 2026 West Michigan Deck Pricing &amp; Planning Guide
              </h2>
              
              <ul className="space-y-4 text-white text-lg mb-8">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl flex-shrink-0 mt-0.5"></i>
                  <span>Real price ranges for wood vs composite decks</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl flex-shrink-0 mt-0.5"></i>
                  <span>Timeline expectations for your project</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl flex-shrink-0 mt-0.5"></i>
                  <span>Common mistakes homeowners make</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl flex-shrink-0 mt-0.5"></i>
                  <span>How to choose the right builder</span>
                </li>
              </ul>

              <button 
                onClick={scrollToForm}
                className="bg-white text-orange-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer shadow-lg inline-flex items-center gap-2"
              >
                <i className="ri-download-line text-xl"></i>
                Get Instant Access + Quote
              </button>
              <p className="text-white/80 text-sm mt-3">Submit your quote request to get the free guide instantly</p>
            </div>

            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-white/10 rounded-2xl blur-xl"></div>
                <img 
                  src="https://static.readdy.ai/image/68c02a6c7db1e4f446ae7031426f220f/0a40ca517630629a58f50600ad76b70a.png"
                  alt="2026 West Michigan Deck Pricing and Planning Guide"
                  className="relative w-80 sm:w-96 lg:w-[420px] rounded-xl shadow-2xl border-4 border-white/20"
                />
                <div className="absolute -bottom-3 -right-3 bg-white text-orange-600 px-4 py-2 rounded-lg font-bold text-sm shadow-lg">
                  FREE DOWNLOAD
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Most Deck Projects Go Over Budget or Take Too Long
            </h2>
            <p className="text-xl text-gray-600">
              You deserve better than the typical contractor experience
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-ghost-line text-2xl text-red-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Contractors Disappear</h3>
              <p className="text-gray-600 text-sm">Mid-project vanishing acts leave you stranded</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-question-line text-2xl text-red-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Unclear Pricing</h3>
              <p className="text-gray-600 text-sm">Hidden costs and surprise charges add up fast</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-tools-line text-2xl text-red-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Cheap Materials</h3>
              <p className="text-gray-600 text-sm">Low-quality lumber leads to costly repairs</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calendar-close-line text-2xl text-red-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Poor Planning</h3>
              <p className="text-gray-600 text-sm">Delays stretch projects from weeks to months</p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              We built a better, more predictable process.
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" id="process">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              A Simpler Way to Build Your Deck
            </h2>
            <p className="text-xl text-gray-600">
              Three straightforward steps from quote to completion
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="relative">
              <div className="bg-orange-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold mb-6 shadow-lg">
                1
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Request Your Quote</h3>
              <p className="text-gray-600 leading-relaxed">
                Fill out our simple form with your project details. We'll review your needs and get back to you within 24 hours with a detailed estimate.
              </p>
            </div>

            <div className="relative">
              <div className="bg-orange-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold mb-6 shadow-lg">
                2
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Review Design + Pricing</h3>
              <p className="text-gray-600 leading-relaxed">
                We'll walk you through material options, design choices, and transparent pricing. No hidden fees, no surprises — just clear information.
              </p>
            </div>

            <div className="relative">
              <div className="bg-orange-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold mb-6 shadow-lg">
                3
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Schedule Your Build</h3>
              <p className="text-gray-600 leading-relaxed">
                Once approved, we'll lock in your timeline and get to work. Our team stays on schedule and keeps you updated every step of the way.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <button 
              onClick={scrollToForm}
              className="bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer shadow-lg"
            >
              Start Your Project Today
            </button>
          </div>
        </div>
      </section>

      {/* Project Types / Services */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" id="services">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Deck Building Services in West Michigan
            </h2>
            <p className="text-xl text-gray-600">
              Expert craftsmanship for every type of deck project
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20composite%20trex%20deck%20with%20gray%20tones%20and%20clean%20lines%20in%20residential%20backyard%20with%20simple%20background%20showing%20premium%20quality%20decking%20material%20and%20professional%20installation%20craftsmanship%20in%20natural%20daylight&width=800&height=600&seq=composite-deck-1&orientation=landscape"
                  alt="Composite deck"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Composite Deck Builder West Michigan</h3>
                <p className="text-gray-600 mb-4">
                  Low-maintenance composite decking that lasts for decades. We install Trex, TimberTech, and other premium brands with expert precision.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-green-600"></i>
                    <span>Fade-resistant materials</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-green-600"></i>
                    <span>No staining or sealing needed</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-green-600"></i>
                    <span>25+ year warranty options</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=beautiful%20natural%20wood%20deck%20with%20warm%20brown%20tones%20in%20residential%20backyard%20with%20simple%20clean%20background%20showing%20quality%20lumber%20construction%20and%20traditional%20craftsmanship%20in%20natural%20outdoor%20lighting&width=800&height=600&seq=wood-deck-1&orientation=landscape"
                  alt="Wood deck"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Wood Deck Construction</h3>
                <p className="text-gray-600 mb-4">
                  Classic wood decks built with premium pressure-treated lumber or cedar. Timeless beauty with proper construction techniques.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-green-600"></i>
                    <span>Premium lumber selection</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-green-600"></i>
                    <span>Natural wood aesthetics</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-green-600"></i>
                    <span>Cost-effective option</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/68c02a6c7db1e4f446ae7031426f220f/866479fa0bd11c1b0b664fd4ad0e5844.png"
                  alt="Trex deck installation"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Trex Deck Installation Grand Rapids</h3>
                <p className="text-gray-600 mb-4">
                  Certified Trex installers bringing you the best in composite decking technology. Multiple color and style options available.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-green-600"></i>
                    <span>Certified installation team</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-green-600"></i>
                    <span>Full product line access</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-green-600"></i>
                    <span>Manufacturer warranty support</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="h-48 overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/68c02a6c7db1e4f446ae7031426f220f/9002319a091fff98f6ddb372679a6f2b.png"
                  alt="Deck replacement"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Deck Replacement &amp; Repair</h3>
                <p className="text-gray-600 mb-4">
                  Restore your existing deck or replace it entirely. We handle everything from minor repairs to complete tear-out and rebuild.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-green-600"></i>
                    <span>Structural assessments</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-green-600"></i>
                    <span>Board replacement</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-green-600"></i>
                    <span>Complete rebuilds</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Proof Gallery */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Quality You Can See
            </h2>
            <p className="text-xl text-gray-600">
              Professional craftsmanship in every project
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="rounded-xl overflow-hidden shadow-lg h-80">
              <img 
                src="https://readdy.ai/api/search-image?query=stunning%20modern%20outdoor%20deck%20with%20elegant%20furniture%20and%20lighting%20in%20residential%20backyard%20with%20simple%20clean%20background%20showing%20premium%20quality%20construction%20and%20beautiful%20design%20in%20evening%20golden%20hour%20lighting&width=800&height=800&seq=deck-gallery-1&orientation=squarish"
                alt="Modern deck design"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg h-80">
              <img 
                src="https://readdy.ai/api/search-image?query=beautiful%20composite%20deck%20with%20modern%20gray%20tones%20and%20clean%20lines%20in%20residential%20setting%20with%20simple%20background%20highlighting%20premium%20materials%20and%20professional%20installation%20quality%20in%20natural%20daylight&width=800&height=800&seq=deck-gallery-2&orientation=squarish"
                alt="Composite deck finish"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg h-80">
              <img 
                src="https://readdy.ai/api/search-image?query=spacious%20outdoor%20living%20deck%20with%20comfortable%20seating%20area%20in%20residential%20backyard%20with%20simple%20background%20showing%20quality%20craftsmanship%20and%20inviting%20design%20in%20warm%20natural%20lighting&width=800&height=800&seq=deck-gallery-3&orientation=squarish"
                alt="Outdoor living space"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#FFFBF5]">
        <div className="max-w-6xl mx-auto">

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <div>
              <div className="inline-flex items-center gap-2 text-orange-700 text-sm font-semibold uppercase tracking-widest mb-4">
                <i className="ri-double-quotes-l text-lg"></i>
                Customer Reviews
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight">
                What West Michigan<br />Homeowners Are Saying
              </h2>
              <p className="text-lg text-gray-500 mt-4 max-w-xl">
                Real feedback from real homeowners across Grand Rapids and surrounding communities.
              </p>
            </div>

            {/* Google Rating Badge */}
            <div className="flex-shrink-0">
              <div className="bg-white rounded-2xl shadow-md border border-gray-100 px-8 py-6 flex items-center gap-5">
                <div className="text-center">
                  <div className="text-5xl font-extrabold text-gray-900 leading-none">4.9</div>
                  <div className="flex gap-0.5 mt-2 justify-center">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                    ))}
                  </div>
                  <div className="text-xs text-gray-400 mt-1 font-medium">Google Rating</div>
                </div>
                <div className="w-px h-16 bg-gray-200"></div>
                <div className="text-center">
                  <div className="text-4xl font-extrabold text-orange-600 leading-none">150+</div>
                  <div className="text-xs text-gray-400 mt-2 font-medium">Happy<br />Homeowners</div>
                </div>
              </div>
            </div>
          </div>

          {/* Review Cards Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">

            {/* Featured Large Card */}
            <div className="md:col-span-1 bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between border-t-4 border-t-orange-500">
              <div>
                <div className="flex gap-1 mb-5">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                  ))}
                </div>
                <i className="ri-double-quotes-l text-5xl text-orange-200 leading-none block mb-2"></i>
                <p className="text-gray-700 leading-relaxed text-base">
                  "We had three quotes and West Michigan Decks was the most transparent about pricing. No hidden fees, no surprises. The Trex deck they built is absolutely stunning — our neighbors keep asking who did it. Finished two days ahead of schedule!"
                </p>
              </div>
              <div className="mt-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-orange-700 font-bold text-lg">MR</span>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Mike &amp; Rachel T.</div>
                    <div className="text-sm text-gray-500">Ada, MI</div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 bg-orange-50 text-orange-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                    <i className="ri-hammer-line"></i>
                    Trex Composite Deck
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-gray-400 font-medium">
                    <span className="text-blue-500 font-bold">G</span> Google Review
                  </span>
                </div>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-white rounded-2xl p-7 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between">
              <div>
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed text-sm">
                  "Our old deck was rotting and we were nervous about the whole replacement process. The team walked us through every step, pulled the permits, and had our new composite deck done in just over a week. Couldn't be happier."
                </p>
              </div>
              <div className="mt-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-green-700 font-bold">SL</span>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 text-sm">Sarah L.</div>
                    <div className="text-xs text-gray-500">Rockford, MI</div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 bg-gray-50 text-gray-600 text-xs font-semibold px-3 py-1.5 rounded-full">
                    <i className="ri-refresh-line"></i>
                    Full Deck Replacement
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-gray-400 font-medium">
                    <span className="text-blue-500 font-bold">G</span> Google Review
                  </span>
                </div>
              </div>
            </div>

            {/* Card 3 */}
            <div className="bg-white rounded-2xl p-7 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between">
              <div>
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed text-sm">
                  "I've hired contractors before and always dreaded the experience. This was completely different. They showed up on time every day, communicated clearly, and the finished deck exceeded our expectations. Highly recommend."
                </p>
              </div>
              <div className="mt-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-amber-700 font-bold">DJ</span>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 text-sm">Dave &amp; Julie K.</div>
                    <div className="text-xs text-gray-500">Cascade, MI</div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 bg-gray-50 text-gray-600 text-xs font-semibold px-3 py-1.5 rounded-full">
                    <i className="ri-home-4-line"></i>
                    New Wood Deck
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-gray-400 font-medium">
                    <span className="text-blue-500 font-bold">G</span> Google Review
                  </span>
                </div>
              </div>
            </div>

            {/* Card 4 */}
            <div className="bg-white rounded-2xl p-7 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between border-t-4 border-t-orange-500">
              <div>
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed text-sm">
                  "Got quotes from four companies. West Michigan Decks was the only one that gave me a detailed breakdown of every cost upfront. The deck is beautiful and the whole process was stress-free. Worth every penny."
                </p>
              </div>
              <div className="mt-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-rose-700 font-bold">TM</span>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 text-sm">Tom M.</div>
                    <div className="text-xs text-gray-500">Grandville, MI</div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 bg-orange-50 text-orange-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                    <i className="ri-hammer-line"></i>
                    Composite Deck Build
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-gray-400 font-medium">
                    <span className="text-blue-500 font-bold">G</span> Google Review
                  </span>
                </div>
              </div>
            </div>

            {/* Card 5 */}
            <div className="bg-white rounded-2xl p-7 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between">
              <div>
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed text-sm">
                  "We wanted a large deck for entertaining and they nailed it. The TimberTech boards look incredible and the built-in bench seating was a great suggestion from their team. Our backyard is completely transformed."
                </p>
              </div>
              <div className="mt-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-teal-700 font-bold">JB</span>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 text-sm">Jennifer B.</div>
                    <div className="text-xs text-gray-500">Wyoming, MI</div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 bg-gray-50 text-gray-600 text-xs font-semibold px-3 py-1.5 rounded-full">
                    <i className="ri-layout-grid-line"></i>
                    Large Entertainment Deck
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-gray-400 font-medium">
                    <span className="text-blue-500 font-bold">G</span> Google Review
                  </span>
                </div>
              </div>
            </div>

            {/* Card 6 */}
            <div className="bg-white rounded-2xl p-7 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between">
              <div>
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed text-sm">
                  "Had them repair and partially replace our aging deck. They were honest about what needed replacing vs. what could be saved, which saved us money. Solid craftsmanship and great communication from start to finish."
                </p>
              </div>
              <div className="mt-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-indigo-700 font-bold">CR</span>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 text-sm">Chris R.</div>
                    <div className="text-xs text-gray-500">Kentwood, MI</div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 bg-gray-50 text-gray-600 text-xs font-semibold px-3 py-1.5 rounded-full">
                    <i className="ri-tools-line"></i>
                    Deck Repair &amp; Rebuild
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-gray-400 font-medium">
                    <span className="text-blue-500 font-bold">G</span> Google Review
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
            <div className="text-center">
              <div className="text-4xl font-extrabold text-orange-600 mb-1">4.9<span className="text-2xl text-gray-400">/5</span></div>
              <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Avg. Rating</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-extrabold text-orange-600 mb-1">150+</div>
              <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Projects Done</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-extrabold text-orange-600 mb-1">98%</div>
              <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Satisfaction Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-extrabold text-orange-600 mb-1">10+</div>
              <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Years in Business</div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <p className="text-gray-500 text-sm mb-4">Join hundreds of happy West Michigan homeowners</p>
            <button
              onClick={scrollToForm}
              className="bg-orange-600 text-white px-10 py-4 rounded-lg text-lg font-bold hover:bg-orange-700 transition-colors whitespace-nowrap cursor-pointer shadow-lg inline-flex items-center gap-2"
            >
              Get My Free Quote
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
            <div className="flex items-center justify-center gap-6 mt-4 text-sm text-gray-500">
              <span className="flex items-center gap-1"><i className="ri-check-line text-green-600"></i> Free Consultation</span>
              <span className="flex items-center gap-1"><i className="ri-check-line text-green-600"></i> Licensed &amp; Insured</span>
              <span className="flex items-center gap-1"><i className="ri-check-line text-green-600"></i> No Obligation</span>
            </div>
          </div>

        </div>
      </section>

      {/* Strong CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-600 to-orange-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://readdy.ai/api/search-image?query=beautiful%20outdoor%20deck%20construction%20with%20tools%20and%20materials%20in%20residential%20setting%20with%20simple%20background%20showing%20professional%20craftsmanship%20and%20quality%20building%20process%20in%20natural%20lighting&width=1920&height=600&seq=cta-bg&orientation=landscape"
            alt="Deck construction"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-5xl font-bold text-white mb-6">
            Get Your Deck Project Started
          </h2>
          <p className="text-xl text-white/90 mb-8">
            No obligation. Fast response. Clear pricing.
          </p>
          <button 
            onClick={scrollToForm}
            className="bg-white text-orange-600 px-10 py-5 rounded-lg text-xl font-bold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer shadow-2xl"
          >
            Request My Free Quote
          </button>
          <p className="text-white/80 mt-6">
            We'll get back to you within 24 hours with pricing and availability
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" id="faq">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about building a deck in West Michigan
            </p>
          </div>

          <div className="space-y-6">
            <details className="bg-gray-50 rounded-xl p-6 cursor-pointer group">
              <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>How much does a deck cost in West Michigan?</span>
                <i className="ri-arrow-down-s-line text-2xl group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="text-gray-600 mt-4 leading-relaxed">
                Deck costs vary based on size, materials, and complexity. A typical wood deck ranges from $25-$45 per square foot, while composite decks run $40-$70 per square foot. For a 300 sq ft deck, expect $7,500-$21,000. We provide detailed quotes with transparent pricing so you know exactly what to expect.
              </p>
            </details>

            <details className="bg-gray-50 rounded-xl p-6 cursor-pointer group">
              <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>How long does it take to build a deck?</span>
                <i className="ri-arrow-down-s-line text-2xl group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="text-gray-600 mt-4 leading-relaxed">
                Most residential decks take 1-2 weeks to complete once we start construction. The timeline depends on deck size, design complexity, and weather conditions. We'll provide a specific timeline in your quote and keep you updated throughout the process.
              </p>
            </details>

            <details className="bg-gray-50 rounded-xl p-6 cursor-pointer group">
              <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>Do you offer composite decking?</span>
                <i className="ri-arrow-down-s-line text-2xl group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="text-gray-600 mt-4 leading-relaxed">
                Yes! We install all major composite brands including Trex, TimberTech, and Fiberon. Composite decking offers low maintenance, long-lasting durability, and comes with extensive warranties. We'll help you choose the best material for your budget and lifestyle.
              </p>
            </details>

            <details className="bg-gray-50 rounded-xl p-6 cursor-pointer group">
              <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>When can you start my project?</span>
                <i className="ri-arrow-down-s-line text-2xl group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="text-gray-600 mt-4 leading-relaxed">
                Our availability varies by season. Spring and summer are our busiest times, so booking early is recommended. Fall projects often have shorter wait times. Once you request a quote, we'll let you know our current schedule and lock in your preferred timeline.
              </p>
            </details>

            <details className="bg-gray-50 rounded-xl p-6 cursor-pointer group">
              <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>Do I need a permit to build a deck?</span>
                <i className="ri-arrow-down-s-line text-2xl group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="text-gray-600 mt-4 leading-relaxed">
                Most deck projects in West Michigan require a building permit. Requirements vary by city and township. We handle all permit applications and ensure your deck meets local building codes and regulations.
              </p>
            </details>

            <details className="bg-gray-50 rounded-xl p-6 cursor-pointer group">
              <summary className="font-bold text-lg text-gray-900 cursor-pointer list-none flex items-center justify-between">
                <span>What areas do you serve?</span>
                <i className="ri-arrow-down-s-line text-2xl group-open:rotate-180 transition-transform"></i>
              </summary>
              <p className="text-gray-600 mt-4 leading-relaxed">
                We serve Grand Rapids and all surrounding West Michigan communities including Wyoming, Kentwood, Rockford, Ada, Cascade, Grandville, Jenison, Hudsonville, and more. Contact us to confirm service in your specific area.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <h3 className="text-2xl font-bold mb-4">West Michigan Decks</h3>
              <p className="text-gray-400 mb-4">
                Professional deck building services for homeowners throughout West Michigan.
              </p>
              <div className="flex items-center gap-2 text-gray-400">
                <i className="ri-phone-line"></i>
                <span>(616) 555-DECK</span>
              </div>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/" className="hover:text-white transition-colors cursor-pointer">Home</a></li>
                <li><a href="/portfolio" className="hover:text-white transition-colors cursor-pointer">Portfolio</a></li>
                <li><a href="/#services" className="hover:text-white transition-colors cursor-pointer">Services</a></li>
                <li><a href="/#process" className="hover:text-white transition-colors cursor-pointer">Process</a></li>
                <li><a href="/#faq" className="hover:text-white transition-colors cursor-pointer">FAQ</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Grand Rapids</li>
                <li>Wyoming</li>
                <li>Kentwood</li>
                <li>Rockford</li>
                <li>Ada</li>
                <li>Cascade</li>
                <li>Grandville</li>
                <li>Jenison</li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-lg mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>New Deck Construction</li>
                <li>Composite Deck Installation</li>
                <li>Trex Deck Installation</li>
                <li>Wood Deck Building</li>
                <li>Deck Replacement</li>
                <li>Deck Repair</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © 2026 West Michigan Decks. All rights reserved.
            </p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer"
            >
              Website Builder
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
